import styles from './MealReliefModal.module.scss'
import ArrowRightIcon from '../assets/arrow-right.svg'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { validateBusTime } from '../helpers/validateTime'
import clsx from 'clsx'
import { getTimeDifferenceInMinutes } from '../helpers/timeDiff'
import { ControlSheetNotes } from './ControlSheetNotes'
import { useCreateMealReliefAnnotation } from '../hooks/useCreateMealReliefAnnotation'
import { ControlSheetAnnotation, ControlSheetNote } from '../types/types'
import { annotationMapper } from '../helpers/annotationMapper'
import { usePatchAnnotation } from '../hooks/usePatchAnnotation'
import { displayTime } from '../helpers/displayTime'

interface Props {
    duty: string
    trip: string
    run: string
    route: string
    scheduled: string
    index: number
    reliefStartScheduled: string
    nextTrip: string
    nextTripScheduled: string
    nextTripRoute?: string
    minMealRelief: number
    addTempNote: (note: ControlSheetNote) => void
    deleteTempNote: (noteId: string) => void
    addTempAnnotation: (annotation: ControlSheetAnnotation) => void
    annotation?: ControlSheetAnnotation
}

export const MealReliefModal = ({
    duty,
    trip,
    run,
    route,
    scheduled,
    index,
    reliefStartScheduled,
    nextTrip,
    nextTripScheduled,
    nextTripRoute,
    minMealRelief,
    addTempNote,
    deleteTempNote,
    addTempAnnotation,
    annotation,
}: Props) => {
    const [reliefStartAmended, setReliefStartAmended] = useState<string>('')
    const [reliefEndAmended, setReliefEndAmended] = useState<string>('')
    const [reliefStartTimeValid, setReliefStartTimeValid] = useState<boolean>(true)
    const [reliefEndTimeValid, setReliefEndTimeValid] = useState<boolean>(true)
    const [isAnnotationPending, setIsAnnotationPending] = useState(false)

    const pendingCallback = useRef<(() => Promise<ControlSheetAnnotation | undefined>) | null>(null)
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)
    const startInputRef = useRef<HTMLInputElement>(null)
    const endInputRef = useRef<HTMLInputElement>(null)

    const annotationIdentifier = `${index}-${scheduled}`

    const mealReliefTime = useMemo(() => {
        // Calculate meal relief time based on the values provided
        const amendedStartValidated = validateBusTime(reliefStartAmended)
        const amendedEndValidated = validateBusTime(reliefEndAmended)
        const startToUse = amendedStartValidated || reliefStartScheduled
        const endToUse = amendedEndValidated || nextTripScheduled

        return getTimeDifferenceInMinutes(startToUse, endToUse) || 0
    }, [reliefStartAmended, reliefEndAmended, reliefStartScheduled, nextTripScheduled])

    // Initialize form values based on annotation
    useEffect(() => {
        setReliefStartAmended(annotation?.startTime || '')
        setReliefEndAmended(annotation?.departureTime || '')
    }, [annotation])

    // Focus the input when component mounts
    useEffect(() => {
        if (startInputRef.current) {
            startInputRef.current.focus()
            setTimeout(() => {
                startInputRef.current?.select()
            }, 0)
        }
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
            if (pendingCallback.current) {
                pendingCallback.current()
            }
        }
    }, [])

    const { handleCreateMealReliefAnnotationAsync } = useCreateMealReliefAnnotation()
    const { handlePatchMealReliefAnnotationAsync } = usePatchAnnotation()

    const createOrPatchAnnotation = useCallback(
        async (
            startTime: string | null,
            endTime: string | null,
            existingAnnotation?: ControlSheetAnnotation
        ): Promise<ControlSheetAnnotation | undefined> => {
            try {
                if (!existingAnnotation) {
                    const newAnnotationDto = await handleCreateMealReliefAnnotationAsync(
                        duty,
                        trip,
                        run,
                        route,
                        annotationIdentifier,
                        startTime ?? undefined,
                        endTime ?? undefined
                    )

                    if (newAnnotationDto.id) {
                        return annotationMapper()(newAnnotationDto)
                    }
                } else {
                    const patchedAnnotationDto = await handlePatchMealReliefAnnotationAsync(
                        existingAnnotation.id,
                        startTime ?? undefined,
                        endTime ?? undefined
                    )

                    if (patchedAnnotationDto.id) {
                        return annotationMapper()(patchedAnnotationDto)
                    }
                }
            } catch (error) {
                console.error(`Error creating or patching meal relief annotation:`, error)
                throw error
            }
        },
        [
            handleCreateMealReliefAnnotationAsync,
            handlePatchMealReliefAnnotationAsync,
            duty,
            trip,
            run,
            route,
            annotationIdentifier,
        ]
    )

    const processAnnotationRequest = useCallback(
        async (startTime: string | null, endTime: string | null, existingAnnotation?: ControlSheetAnnotation) => {
            setIsAnnotationPending(true)

            let resultAnnotation: ControlSheetAnnotation | undefined
            try {
                resultAnnotation = await createOrPatchAnnotation(startTime, endTime, existingAnnotation)

                if (resultAnnotation) {
                    addTempAnnotation(resultAnnotation)
                }
            } catch (error) {
                console.error(`Failed to process meal relief annotation request:`, error)
            } finally {
                setIsAnnotationPending(false)
            }
            return resultAnnotation
        },
        [createOrPatchAnnotation, addTempAnnotation]
    )

    const hasAnnotationChanged = useCallback(
        (startTime: string | null, endTime: string | null): boolean => {
            if (!annotation && !startTime && !endTime) {
                return false
            }

            if (annotation) {
                return annotation.startTime !== startTime || annotation.departureTime !== endTime
            }

            return true
        },
        [annotation]
    )

    const updateDebounced = useCallback(
        (start: string, end: string) => {
            const theStartTime = start.trim().length > 0 ? start : null
            const theEndTime = end.trim().length > 0 ? end : null

            // Clear existing timer
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }

            // Only proceed if times are valid and values have changed
            if (!reliefStartTimeValid || !reliefEndTimeValid || !hasAnnotationChanged(theStartTime, theEndTime)) {
                pendingCallback.current = null
                return
            }

            // Create and store callback
            const callback = async () => {
                pendingCallback.current = null
                return await processAnnotationRequest(theStartTime, theEndTime, annotation)
            }

            pendingCallback.current = callback
            debounceTimer.current = setTimeout(callback, 3000)
        },
        [reliefStartTimeValid, reliefEndTimeValid, hasAnnotationChanged, processAnnotationRequest, annotation]
    )

    const checkMoveFocus = (text: string) => {
        if (text.trim().length >= 5 && validateBusTime(text)) {
            if (endInputRef.current) {
                endInputRef.current.focus()
                setTimeout(() => {
                    endInputRef.current?.select()
                }, 0)
            }
        }
    }

    const handleCreateAnnotationForNotes = async () => {
        // Clear any pending debounced updates
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current)
        }

        // Execute any pending callback immediately
        if (pendingCallback.current) {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current)
            }
            return await pendingCallback.current()
        }

        setIsAnnotationPending(true)

        try {
            const annotationDto = await handleCreateMealReliefAnnotationAsync(
                duty,
                trip,
                run,
                route,
                annotationIdentifier
            )

            return annotationMapper()(annotationDto)
        } catch (error) {
            console.error(`Failed to create meal relief annotation for notes:`, error)
            throw new Error('Failed to create annotation')
        } finally {
            setIsAnnotationPending(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerTitle}>Meal Relief</div>
                <div className={styles.headerSubtitle}>Duty {duty}</div>
            </div>
            <div className={styles.timesContainer}>
                <div className={styles.timeHeaders}>
                    <div className={styles.timeEntryHeader}>Relief start</div>
                    <div className={styles.timeEntryHeader}>
                        <ArrowRightIcon />
                        <div className={styles.nextTrip}>
                            <div>Next trip {nextTrip}</div>
                            {nextTripRoute && <div className={styles.nextTripRoute}>Route {nextTripRoute}</div>}
                        </div>
                    </div>
                </div>
                <div className={styles.timesRow}>
                    <div className={styles.timeEntry}>
                        <div className={styles.timeEntryContent}>
                            <span>Scheduled</span>
                            <span>{displayTime(reliefStartScheduled)}</span>
                        </div>
                        <div className={styles.timeEntryContent}>
                            <span>Amended</span>
                            <span>
                                <input
                                    ref={startInputRef}
                                    type="text"
                                    value={reliefStartAmended}
                                    maxLength={5}
                                    onChange={(e) => {
                                        setReliefStartTimeValid(true)
                                        setReliefStartAmended(e.target.value.trim())
                                        checkMoveFocus(e.target.value.trim())
                                    }}
                                    onFocus={() => {
                                        setReliefStartTimeValid(true)
                                        if (debounceTimer.current) {
                                            clearTimeout(debounceTimer.current)
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value.trim()

                                        if (value.length > 0) {
                                            const isValid = validateBusTime(value)
                                            if (isValid) {
                                                setReliefStartAmended(isValid)
                                                updateDebounced(isValid, reliefEndAmended)
                                            }
                                            setReliefStartTimeValid(isValid !== null)
                                        } else {
                                            setReliefStartAmended('')
                                            updateDebounced('', reliefEndAmended)
                                            setReliefStartTimeValid(true)
                                        }
                                    }}
                                    placeholder="Time"
                                    className={clsx(styles.textField, {
                                        [styles.invalid]: !reliefStartTimeValid,
                                    })}
                                />
                            </span>
                        </div>
                        {!reliefStartTimeValid && (
                            <div className={styles.invalidWarning}>Please enter a valid time</div>
                        )}
                    </div>
                    <div className={styles.timeEntry}>
                        <div className={styles.timeEntryContent}>
                            <span>Scheduled</span>
                            <span>{displayTime(nextTripScheduled)}</span>
                        </div>
                        <div className={styles.timeEntryContent}>
                            <span>Amended</span>
                            <span>
                                <input
                                    ref={endInputRef}
                                    type="text"
                                    value={reliefEndAmended}
                                    maxLength={5}
                                    onChange={(e) => {
                                        setReliefEndTimeValid(true)
                                        setReliefEndAmended(e.target.value.trim())
                                    }}
                                    onFocus={() => {
                                        setReliefEndTimeValid(true)
                                        if (debounceTimer.current) {
                                            clearTimeout(debounceTimer.current)
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value.trim()
                                        if (value.length > 0) {
                                            const isValid = validateBusTime(value)
                                            if (isValid) {
                                                setReliefEndAmended(isValid)
                                                updateDebounced(reliefStartAmended, isValid)
                                            }
                                            setReliefEndTimeValid(isValid !== null)
                                        } else {
                                            setReliefEndAmended('')
                                            updateDebounced(reliefStartAmended, '')
                                            setReliefEndTimeValid(true)
                                        }
                                    }}
                                    placeholder="Time"
                                    className={clsx(styles.textField, {
                                        [styles.invalid]: !reliefEndTimeValid,
                                    })}
                                />
                            </span>
                        </div>

                        {!reliefEndTimeValid && <div className={styles.invalidWarning}>Please enter a valid time</div>}
                    </div>
                </div>
                <div className={styles.mealRelief}>
                    <span>Meal relief</span>
                    <span>{mealReliefTime} mins</span>
                </div>
                {mealReliefTime < minMealRelief && (
                    <div className={styles.invalidWarning}>Less than minimum meal relief time</div>
                )}
            </div>
            <div className={styles.notesContainer}>
                <ControlSheetNotes
                    createAnnotation={handleCreateAnnotationForNotes}
                    annotationId={annotation?.id}
                    existingNotes={annotation?.notes || []}
                    onUpdate={() => {}}
                    addTempNote={addTempNote}
                    deleteTempNote={deleteTempNote}
                    addTempAnnotation={addTempAnnotation}
                    isAnnotationPending={isAnnotationPending}
                />
            </div>
        </div>
    )
}
